import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import { Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { ContactDetails } from "@/features/contact/contact-details";
import { EditContactDialog } from "@/features/contact/edit-contact/dialog";
import { contactQuery } from "@/features/contact/queries";
import { ApiError } from "@/lib/api-client";
import { m } from "@/lib/paraglide/messages.js";

// /contacts/:contactId: one contact, read, with the same edit dialog as the list's rows. The
// API's 404 (another reseller's id, or none at all) is this route's not-found page. The crumb
// is the contact's own name.
export const Route = createFileRoute("/_authenticated/contacts/$contactId")({
  loader: async ({ context, params }) => {
    try {
      const contact = await context.queryClient.ensureQueryData(contactQuery(params.contactId));
      return { crumb: contact.name };
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 422)) throw notFound();
      throw error;
    }
  },
  component: ContactPage,
  notFoundComponent: ContactNotFound,
});

function ContactPage() {
  const { contactId } = Route.useParams();
  const { reseller } = Route.useRouteContext();
  const { data: contact } = useSuspenseQuery(contactQuery(contactId));
  return (
    <>
      <Item>
        <ItemContent>
          <ItemTitle>{contact.name}</ItemTitle>
          {contact.organisation && <ItemDescription>{contact.organisation}</ItemDescription>}
        </ItemContent>
        {reseller.status === "active" && (
          <ItemActions>
            <EditContactDialog contact={contact} />
          </ItemActions>
        )}
      </Item>
      <ContactDetails contact={contact} />
    </>
  );
}

function ContactNotFound() {
  return (
    <Empty>
      <EmptyHeader>
        <EmptyTitle>{m.contacts_not_found_title()}</EmptyTitle>
        <EmptyDescription>{m.contacts_not_found_description()}</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button variant="outline" render={<Link to="/contacts" />}>
          {m.contacts_back()}
        </Button>
      </EmptyContent>
    </Empty>
  );
}
